import { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  TooltipProps,
} from 'recharts';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  getReferralOverview,
  getReferralDaily,
  getReferralByApp,
  getReferralByCountry,
  getReferralByAdType,
  getRecentClicks,
  formatNumber,
  type ReferralOverview,
  type ReferralDailyStats,
  type ReferralByApp,
  type ReferralByCountry,
  type ReferralByAdType,
  type RecentClicksResponse,
} from '@/api/referrals';
import { useQuery } from '@tanstack/react-query';
import { formatDateTime } from '@/lib/datetime';

type DateRange = '7d' | '30d' | '90d';

const dateRangeOptions: { value: DateRange; label: string; days: number }[] = [
  { value: '7d', label: '7 Days', days: 7 },
  { value: '30d', label: '30 Days', days: 30 },
  { value: '90d', label: '90 Days', days: 90 },
];

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-surface/95 backdrop-blur border border-border rounded-xl shadow-lg p-3 min-w-[140px]">
        <p className="text-xs font-medium text-content-subtle mb-1.5">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm flex items-center gap-2" style={{ color: entry.color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
            {entry.name}:{' '}
            <span className="font-semibold ml-auto">
              {formatNumber(entry.value as number)}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function StatCard({ label, value, icon, color }: { label: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-medium text-content-subtle">{label}</p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold text-content font-mono tracking-tight">{value}</p>
    </div>
  );
}

const statusLabels: Record<string, string> = {
  pending: 'Ad Click',
  delivered: 'Installed from Ad',
  expired: 'Expired',
};

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: 'bg-warning-soft text-warning border-warning/30',
    delivered: 'bg-success-soft text-success border-success/30',
    expired: 'bg-surface-muted text-content-subtle border-border',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${styles[status] || styles.pending}`}>
      {statusLabels[status] || status}
    </span>
  );
}

function getFlagEmoji(countryCode: string): string {
  if (!countryCode || countryCode === 'XX' || countryCode === 'Unknown') return '\u{1F30D}';
  const codePoints = countryCode.toUpperCase().split('').map((c) => 127397 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

const refChartSeries = [
  { key: 'clicks', label: 'Ad Clicks', color: '#635BFF' },
  { key: 'delivered', label: 'Installed from Ad', color: '#10B981' },
  { key: 'expired', label: 'Expired', color: '#F59E0B' },
];

function ReferralChart({ data }: { data: ReferralDailyStats[] }) {
  const themeColors = useThemeColors();
  const [visible, setVisible] = useState<Record<string, boolean>>({
    clicks: true,
    delivered: true,
    expired: false,
  });

  const toggle = (key: string) => setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="bg-surface rounded-xl border border-border shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-content">Daily Overview</h3>
        <div className="flex items-center gap-2">
          {refChartSeries.map((s) => (
            <button
              key={s.key}
              onClick={() => toggle(s.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                visible[s.key]
                  ? 'bg-surface-muted text-content shadow-sm'
                  : 'bg-transparent text-content-subtle hover:text-content-subtle'
              }`}
            >
              <span
                className="w-2.5 h-2.5 rounded-full transition-opacity"
                style={{
                  backgroundColor: s.color,
                  opacity: visible[s.key] ? 1 : 0.3,
                }}
              />
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <defs>
            {refChartSeries.map((s) => (
              <linearGradient key={s.key} id={`gRef_${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={s.color} stopOpacity={0.15} />
                <stop offset="95%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={themeColors.grid} />
          <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: themeColors.axis }} />
          <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: themeColors.axis }} />
          <Tooltip content={<CustomTooltip />} />
          {refChartSeries.map((s) =>
            visible[s.key] ? (
              <Area
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#gRef_${s.key})`}
              />
            ) : null
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Referrals() {
  const [dateRange, setDateRange] = useState<DateRange>('30d');

  const days = useMemo(
    () => dateRangeOptions.find((o) => o.value === dateRange)?.days || 30,
    [dateRange],
  );

  const { data: overview } = useQuery<ReferralOverview>({
    queryKey: ['referral-overview', days],
    queryFn: () => getReferralOverview(days),
  });

  const { data: dailyData = [], isLoading } = useQuery<ReferralDailyStats[]>({
    queryKey: ['referral-daily', days],
    queryFn: () => getReferralDaily(days),
  });

  const { data: byApp = [] } = useQuery<ReferralByApp[]>({
    queryKey: ['referral-by-app', days],
    queryFn: () => getReferralByApp(days),
  });

  const { data: byCountry = [] } = useQuery<ReferralByCountry[]>({
    queryKey: ['referral-by-country', days],
    queryFn: () => getReferralByCountry(days),
  });

  const { data: byAdType = [] } = useQuery<ReferralByAdType[]>({
    queryKey: ['referral-by-ad-type', days],
    queryFn: () => getReferralByAdType(days),
  });

  const [clicksPage, setClicksPage] = useState(0);
  const clicksPerPage = 50;

  const { data: recentClicksData } = useQuery<RecentClicksResponse>({
    queryKey: ['referral-recent', clicksPage],
    queryFn: () => getRecentClicks(clicksPage, clicksPerPage),
    placeholderData: (prev) => prev,
  });

  const recentClicks = recentClicksData?.items || [];
  const totalClicks = recentClicksData?.total || 0;
  const totalClicksPages = Math.ceil(totalClicks / clicksPerPage);

  if (isLoading && !overview) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-surface-muted rounded-lg w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-surface rounded-xl border border-border p-5 h-28">
              <div className="h-3 bg-surface-muted rounded w-20 mb-3" />
              <div className="h-7 bg-surface-muted rounded w-28" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasData = dailyData.some((d) => d.clicks > 0 || d.delivered > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-content">Referrals</h1>
        <p className="text-sm text-content-subtle mt-0.5">Cross-app referral click tracking and delivery analytics</p>
      </div>

      {/* Date range */}
      <div className="bg-surface rounded-xl border border-border shadow-sm p-4">
        <div className="flex items-center gap-3">
          <div className="flex bg-surface-muted rounded-lg p-0.5 gap-0.5">
            {dateRangeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDateRange(opt.value)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  dateRange === opt.value
                    ? 'bg-surface text-content shadow-sm'
                    : 'text-content-subtle hover:text-content-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <StatCard
            label="Total Clicks"
            value={formatNumber(overview.total_clicks)}
            color="bg-primary/10"
            icon={
              <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
              </svg>
            }
          />
          <StatCard
            label="Ad Clicks"
            value={formatNumber(overview.pending)}
            color="bg-warning-soft"
            icon={
              <svg className="w-4 h-4 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Installed from Ad"
            value={formatNumber(overview.delivered)}
            color="bg-success-soft"
            icon={
              <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <StatCard
            label="Delivery Rate"
            value={`${overview.delivery_rate.toFixed(1)}%`}
            color="bg-cyan-100"
            icon={
              <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            }
          />
        </div>
      )}

      {!hasData ? (
        <div className="bg-surface rounded-xl border border-border shadow-sm p-16 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-surface-muted mb-4">
            <svg className="w-8 h-8 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
            </svg>
          </div>
          <p className="text-content-muted font-medium">No referral data yet</p>
          <p className="text-sm text-content-subtle mt-1">Data will appear here once cross-app referral clicks start being tracked</p>
        </div>
      ) : (
        <>
          {/* Combined Chart */}
          <ReferralChart data={dailyData} />

          {/* By Ad Type */}
          {byAdType.length > 0 && (
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">Installs by Ad Type</h3>
              </div>
              <div className="p-5 space-y-3">
                {(() => {
                  const adTypeLabels: Record<string, string> = { interstitial: 'Interstitial', rewarded: 'Rewarded', banner: 'Banner', native: 'Custom Banner', popup: 'Popup' };
                  const adTypeColors: Record<string, string> = { interstitial: 'bg-purple-500', rewarded: 'bg-amber-500', banner: 'bg-blue-500', native: 'bg-success', popup: 'bg-orange-500', unknown: 'bg-content-subtle' };
                  const maxClicks = byAdType[0]?.clicks || 1;
                  return byAdType.map((row) => {
                    const pct = ((row.clicks / maxClicks) * 100).toFixed(1);
                    const convRate = row.clicks > 0 ? ((row.delivered / row.clicks) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={row.ad_type} className="flex items-center gap-3">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${adTypeColors[row.ad_type] || adTypeColors.unknown}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-content">{adTypeLabels[row.ad_type] || row.ad_type}</span>
                            <div className="flex items-center gap-3 ml-2">
                              <span className="text-xs text-content-subtle font-mono">{formatNumber(row.clicks)} clicks</span>
                              <span className="text-xs text-success font-mono">{formatNumber(row.delivered)} inst</span>
                              <span className="text-xs text-cyan-600 font-mono">{convRate}%</span>
                            </div>
                          </div>
                          <div className="w-full bg-surface-muted rounded-full h-1.5">
                            <div className={`h-1.5 rounded-full transition-all ${adTypeColors[row.ad_type] || adTypeColors.unknown}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}

          {/* By App & By Country tables */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            {/* By App Pairs */}
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">By App Pair</h3>
              </div>
              {byApp.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-muted/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">Source</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-content-subtle uppercase tracking-wider">Target</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-content-subtle uppercase tracking-wider">Clicks</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-content-subtle uppercase tracking-wider">Installs</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {byApp.map((row, i) => (
                        <tr key={i} className="hover:bg-surface-muted/50 transition-colors">
                          <td className="px-6 py-3.5 text-sm text-content font-mono text-xs">{row.source_bundle_id}</td>
                          <td className="px-6 py-3.5 text-sm text-content font-mono text-xs">{row.target_bundle_id}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(row.clicks)}</td>
                          <td className="px-6 py-3.5 text-right text-sm text-content-muted font-mono">{formatNumber(row.delivered)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-content-subtle">No app pair data</div>
              )}
            </div>

            {/* By Country */}
            <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-sm font-semibold text-content">By Country</h3>
              </div>
              {byCountry.length > 0 ? (
                <div className="p-5 space-y-3">
                  {byCountry.slice(0, 10).map((country) => {
                    const maxClicks = byCountry[0]?.clicks || 1;
                    const pct = ((country.clicks / maxClicks) * 100).toFixed(1);
                    return (
                      <div key={country.country} className="flex items-center gap-3">
                        <span className="text-lg leading-none">{getFlagEmoji(country.country)}</span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-content truncate">{country.country}</span>
                            <div className="flex items-center gap-3 ml-2">
                              <span className="text-xs text-content-subtle font-mono">{formatNumber(country.clicks)} clicks</span>
                              <span className="text-xs text-success font-mono">{formatNumber(country.delivered)} inst</span>
                            </div>
                          </div>
                          <div className="w-full bg-surface-muted rounded-full h-1.5">
                            <div className="bg-purple-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-content-subtle">No country data</div>
              )}
            </div>
          </div>

          {/* Recent Clicks */}
          <div className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-border">
              <h3 className="text-sm font-semibold text-content">Recent Clicks</h3>
            </div>
            {recentClicks.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-muted/50">
                      <tr>
                        {['Device ID', 'Source', 'Target', 'Country', 'Status', 'Created', 'Installed'].map((h, i) => (
                          <th key={h} className={`px-4 py-3 text-xs font-semibold text-content-subtle uppercase tracking-wider ${i === 0 ? 'text-left' : i >= 5 ? 'text-right' : 'text-left'}`}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {recentClicks.map((click) => (
                        <tr key={click.id} className="hover:bg-surface-muted/50 transition-colors">
                          <td className="px-4 py-3 text-xs text-content-muted font-mono truncate max-w-[120px]" title={click.device_id}>
                            {click.device_id.slice(0, 12)}...
                          </td>
                          <td className="px-4 py-3 text-xs text-content font-mono">{click.source_bundle_id}</td>
                          <td className="px-4 py-3 text-xs text-content font-mono">{click.target_bundle_id}</td>
                          <td className="px-4 py-3 text-xs text-content-muted">
                            <span className="mr-1">{getFlagEmoji(click.ip_country)}</span>
                            {click.ip_country}
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={click.status} /></td>
                          <td className="px-4 py-3 text-right text-xs text-content-subtle">{formatDateTime(click.created_at)}</td>
                          <td className="px-4 py-3 text-right text-xs text-content-subtle">
                            {click.delivered_at ? formatDateTime(click.delivered_at) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totalClicksPages > 1 && (
                  <div className="px-6 py-3 border-t border-border flex items-center justify-between">
                    <span className="text-xs text-content-subtle">
                      {clicksPage * clicksPerPage + 1}–{Math.min((clicksPage + 1) * clicksPerPage, totalClicks)} of {totalClicks}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setClicksPage((p) => Math.max(0, p - 1))}
                        disabled={clicksPage === 0}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Prev
                      </button>
                      <span className="text-xs text-content-subtle">{clicksPage + 1} / {totalClicksPages}</span>
                      <button
                        onClick={() => setClicksPage((p) => Math.min(totalClicksPages - 1, p + 1))}
                        disabled={clicksPage >= totalClicksPages - 1}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-content-muted hover:bg-surface-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="px-6 py-10 text-center text-sm text-content-subtle">No recent clicks</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
